/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import {
  Sparkles,
  Search,
  Plus,
  Edit2,
  Filter,
  RotateCw,
} from "lucide-react";

export default function AISections() {
  const [sectionList, setSectionList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const { listOfAllAISections, syncAISections } = useContext(APIContext);

  const fetchSections = () => {
    listOfAllAISections()
      .then((sections) => {
        setSectionList(Array.isArray(sections) ? sections : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to load AI sections");
        setSectionList([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const handleSyncSections = async () => {
    setSyncing(true);
    try {
      await syncAISections();
      await fetchSections();
      toast.success("AI sections synced successfully!");
    } catch (error) {
      console.error("Error syncing AI sections:", error);
      toast.error("Error syncing AI sections: " + (error.message || "Unknown error"));
    } finally {
      setSyncing(false);
    }
  };

  const categories = useMemo(() => {
    const set = new Set();
    sectionList.forEach((s) => {
      const cat = s.section_category || s.category;
      if (cat && typeof cat === "string") set.add(cat.toLowerCase());
    });
    return Array.from(set).sort();
  }, [sectionList]);

  const filteredSections = useMemo(() => {
    return sectionList.filter((section) => {
      const name = (section.section_name || "").toLowerCase();
      const slug = (section.section_slug || "").toLowerCase();
      const q = searchQuery.toLowerCase().trim();
      const matchesSearch = !q || name.includes(q) || slug.includes(q);

      let matchesCat = true;
      if (selectedCategory !== "all") {
        const cat = (section.section_category || section.category || "").toLowerCase();
        matchesCat = cat === selectedCategory;
      }

      return matchesSearch && matchesCat;
    });
  }, [sectionList, searchQuery, selectedCategory]);

  return (
    <>
      <HelmetLayout title="AI Sections" />
      <div className="space-y-6 pb-12">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">AI Sections</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Explore, manage, and synchronize AI-generated page sections.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleSyncSections}
              disabled={syncing}
              className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RotateCw size={15} className={syncing ? "animate-spin" : ""} />
              {syncing ? "Syncing..." : "Sync Sections"}
            </button>
            <Link
              to="/ai-sections/add"
              className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20 flex items-center gap-2"
            >
              <Plus size={16} />
              Add AI Section
            </Link>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Total AI Sections</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : sectionList.length}
              </h3>
            </div>
            <div className="p-3 bg-[#623ce7]/10 text-[#623ce7] rounded-xl">
              <Sparkles size={22} />
            </div>
          </div>
          <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-slate-500 mb-1">Categories</p>
              <h3 className="text-2xl font-bold text-slate-900">
                {loading ? "—" : categories.length}
              </h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <Filter size={22} />
            </div>
          </div>
        </div>

        {/* Search & Category Filter Bar */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm space-y-4">
          {/* Full Width Search Bar */}
          <div className="relative w-full">
            <Search
              size={18}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Search AI sections by name or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:bg-white focus:ring-2 focus:ring-[#623ce7] focus:outline-none transition-all"
            />
          </div>

          {/* Dedicated Category Pills Row */}
          {categories.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide pt-1 border-t border-slate-100">
              <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider shrink-0 mr-1">
                Category:
              </span>
              <button
                onClick={() => setSelectedCategory("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors shrink-0 ${
                  selectedCategory === "all"
                    ? "bg-[#623ce7] text-white shadow-sm shadow-[#623ce7]/30"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                All ({sectionList.length})
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold capitalize whitespace-nowrap transition-colors shrink-0 ${
                    selectedCategory === cat
                      ? "bg-[#623ce7] text-white shadow-sm shadow-[#623ce7]/30"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  {cat.replace(/-/g, " ")}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sections Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200">
            <PulseLoader size={12} color="#623ce7" />
            <p className="text-slate-400 text-sm mt-4">Loading AI sections...</p>
          </div>
        ) : filteredSections.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200 shadow-sm">
            <Sparkles size={36} className="mx-auto text-slate-300 mb-3" />
            <h3 className="text-base font-semibold text-slate-700 mb-1">No AI sections found</h3>
            <p className="text-slate-500 text-sm max-w-sm mx-auto">
              {searchQuery || selectedCategory !== "all"
                ? "Try adjusting your search query or category filter."
                : "No AI sections have been synchronized or created yet."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredSections.map((section) => {
              const categoryName =
                section.section_category || section.category || "General";
              const thumbnail =
                section.section_thumbnail ||
                "https://prod.grigora-cdn.com/defaults/img/webclip.png";

              return (
                <div
                  key={section.section_id}
                  className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md hover:border-slate-300 transition-all flex flex-col group"
                >
                  {/* Thumbnail & Hover Overlay */}
                  <div className="relative aspect-[16/10] bg-slate-100 overflow-hidden border-b border-slate-100">
                    <img
                      src={thumbnail}
                      alt={section.section_name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://via.placeholder.com/400x250?text=AI+Section";
                      }}
                    />

                    {/* Category badge */}
                    <div className="absolute top-2.5 left-2.5">
                      <span className="px-2.5 py-1 rounded-md text-[11px] font-semibold bg-white/95 backdrop-blur-sm text-slate-700 shadow-sm capitalize">
                        {categoryName.replace(/-/g, " ")}
                      </span>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center gap-3 p-4">
                      <Link
                        to={`/ai-sections/${section.section_id}`}
                        className="bg-white hover:bg-slate-100 text-slate-900 text-xs font-semibold px-4 py-2 rounded-lg transition-colors flex items-center gap-1.5 shadow-sm"
                      >
                        <Edit2 size={13} />
                        Edit Section
                      </Link>
                    </div>
                  </div>

                  {/* Card Content */}
                  <div className="p-4 flex-1 flex flex-col justify-between">
                    <div>
                      <h4
                        className="font-semibold text-slate-900 text-sm group-hover:text-[#623ce7] transition-colors truncate"
                        title={section.section_name}
                      >
                        {section.section_name || "Untitled AI Section"}
                      </h4>
                      {section.section_description && (
                        <p className="text-xs text-slate-500 line-clamp-2 mt-1">
                          {section.section_description}
                        </p>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-400">
                      <span className="font-mono text-[11px]">
                        ID: {String(section.section_id || "").slice(0, 10)}
                      </span>
                      <Link
                        to={`/ai-sections/${section.section_id}`}
                        className="text-[#623ce7] hover:underline font-medium text-xs flex items-center gap-1"
                      >
                        Details &rarr;
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
